import React from "react";
import DatePicker from "react-datepicker";
import { BiSolidError } from "react-icons/bi";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { FaCaretDown } from "react-icons/fa";
import { FaRegCalendarAlt, FaTimes } from "react-icons/fa";
import { useState, useEffect, useMemo, useRef } from "react";
import { parse, isValid } from "date-fns";
import { toZonedTime, formatInTimeZone } from "date-fns-tz";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import "../../styles/globals.css";

const addDatePickerStyles = () => {
  const style = document.createElement("style");
  style.textContent = `
    .custom-datepicker-popper {
      z-index: 99999 !important;
    }
    .react-datepicker-popper {
      z-index: 99999 !important;
    }
  `;
  document.head.appendChild(style);
};

const parseDateValue = (val) => {
  if (!val) return null;
  try {
    return typeof val === "string" ? parseISO(val) : new Date(val);
  } catch {
    return null;
  }
};

/**
 * @component RadioInput
 * @description A yes/no radio input component with support for disabled state and showing previous values.
 *
 * @param {string} label - The label displayed above the radio buttons.
 * @param {string} name - The name attribute for the radio group.
 * @param {boolean} value - The currently selected value (true/false).
 * @param {Function} onChange - Function to handle change events.
 * @param {boolean} [disabled=false] - Whether the input is disabled.
 * @param {string} [previousValue] - Optional previous value to display as a warning.
 */
const RadioInput = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  previousValue,
}) => {
  return (
    <div className="w-full max-w-[310px]">
      <label className="block h-[16px] text-[#35424D] mb-2 text-left">
        {label}
      </label>
      <div className="flex mt-3">
        <div
          className={`flex items-center p-2 h-[40px] border border-[#C3C3C3] rounded w-1/2 mr-2 ${
            disabled
              ? "border-[#C3C3C3] bg-[#F5F5F5]"
              : "border-[#C3C3C3] focus-within:border-[#35424D] focus-within:outline-none"
          } `}
        >
          <input
            type="radio"
            name={name}
            value="true"
            checked={value === true}
            onChange={() =>
              !disabled && onChange({ target: { name, value: true } })
            }
            className="mr-2  accent-[#35424D]"
            disabled={disabled}
          />
          Yes
        </div>
        <div
          className={`flex items-center h-[40px] p-2 border border-[#C3C3C3] rounded w-1/2 ${
            disabled
              ? "border-[#C3C3C3] bg-[#F5F5F5]"
              : "border-[#C3C3C3] focus-within:border-[#35424D] focus-within:outline-none"
          }`}
        >
          <input
            type="radio"
            name={name}
            value="false"
            checked={value === false}
            onChange={() =>
              !disabled && onChange({ target: { name, value: false } })
            }
            className="mr-2  accent-[#35424D]"
            disabled={disabled}
          />
          No
        </div>
      </div>
      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

/**
 * @component TextInput
 * @description A styled text input component.
 *
 * @param {string} label - The label for the input.
 * @param {string} name - The input name.
 * @param {string} value - The input value.
 * @param {Function} onChange - Change handler.
 * @param {string} [placeholder=""] - Input placeholder.
 * @param {boolean} [disabled=false] - Whether the input is disabled.
 * @param {boolean} [required=false] - Whether the input is required.
 * @param {string} [error=""] - Optional error message.
 * @param {string} [previousValue] - Optional previous value to show a warning.
 */
const TextInput = ({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  disabled = false,
  required = false,
  error = "",
  previousValue,
  important = false,
  maxLength,
}) => {
  return (
    <div>
      <label className="block h-[16px] mb-2 text-[#35424D] text-left">
        {label}
        {(important || required) && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        className={`text-[#35424D] w-full max-w-[310px] h-[40px] mt-1 p-2 border rounded-md ${
          error ? "border-red-500" : "border-[#C3C3C3]"
        } ${disabled ? "border-[#C3C3C3] bg-[#F5F5F5] cursor-not-allowed" : ""}
         focus:border-[#35424D] focus:border focus:outline-none`}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

/**
 * @component Text Area
 * @description A styled text area component.
 *
 * @param {string} label - The label for the input.
 * @param {string} name - The input name.
 * @param {string} value - The input value.
 * @param {Function} onChange - Change handler.
 * @param {string} [placeholder=""] - Input placeholder.
 * @param {boolean} [disabled=false] - Whether the input is disabled.
 * @param {boolean} [required=false] - Whether the input is required.
 * @param {string} [error=""] - Optional error message.
 * @param {string} [previousValue] - Optional previous value to show a warning.
 */
const TextArea = ({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  disabled = false,
  required = false,
  error = "",
  previousValue,
  important = false,
  maxLength,
}) => {
  return (
    <div>
      <label className="block h-[16px] mb-2 text-[#35424D] text-left">
        {label}
        {(important || required) && <span className="text-red-500">*</span>}
      </label>
      <textarea
        type="text"
        className={`text-[#35424D] w-full h-[100px] mt-1 p-2 border rounded-md resize-none ${
          error ? "border-red-500" : "border-[#C3C3C3]"
        } ${disabled ? "border-[#C3C3C3] bg-[#F5F5F5] cursor-not-allowed" : ""}
         focus:border-[#35424D] focus:border focus:outline-none`}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        maxLength={maxLength}
      />
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

/**
 * @component DateInput
 * @description A styled date input with optional previous value warning.
 *
 * @param {string} label - The input label.
 * @param {string} name - The name attribute.
 * @param {string} value - The date value in YYYY-MM-DD format.
 * @param {Function} onChange - Change handler function.
 * @param {boolean} [disabled=false] - Whether the field is disabled.
 * @param {string} [previousValue] - Previous value to show for tracking changes.
 */
const DateInput = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  previousValue,
}) => {
  const dateValue = parseDateValue(value);

  useEffect(() => {
    addDatePickerStyles();
  }, []);

  const handleKeyDown = (e) => {
    if (
      ![
        "Tab",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key) &&
      !(e.ctrlKey || e.metaKey)
    ) {
      e.preventDefault();
    }
  };

  const handleChangeRaw = (e) => {
    const pastedValue = e.target.value;

    const normalizedValue = pastedValue?.replace(/-/g, "/");

    const parts = normalizedValue?.split("/");
    if (parts?.length === 3) {
      const month = parts[0].padStart(2, "0");
      const day = parts[1].padStart(2, "0");
      const year = parts[2];

      const formattedValue = `${month}/${day}/${year}`;

      const parsedDate = parse(formattedValue, "MM/dd/yyyy", new Date());
      if (isValid(parsedDate)) {
        onChange({ target: { name, value: format(parsedDate, "yyyy-MM-dd") } });
      }
    }
  };

  const displayValue =
    dateValue && isValid(dateValue) ? format(dateValue, "MM/dd/yyyy") : "";

  const handleClear = () => {
    onChange({ target: { name, value: null } });
  };

  const CustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="flex items-center justify-between px-2 py-2 bg-white border-b">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="p-1 hover:bg-gray-100 rounded"
      >
        ←
      </button>
      <div className="flex items-center space-x-2">
        <select
          value={date.getMonth()}
          onChange={({ target: { value } }) => changeMonth(parseInt(value))}
          className="text-sm border rounded px-2 py-1"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i}>
              {new Date(0, i).toLocaleDateString("en", { month: "long" })}
            </option>
          ))}
        </select>
        <select
          value={date.getFullYear()}
          onChange={({ target: { value } }) => changeYear(parseInt(value))}
          className="text-sm border rounded px-2 py-1"
        >
          {Array.from({ length: 10 }, (_, i) => {
            const year = new Date().getFullYear() - 5 + i;
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>
      </div>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="p-1 hover:bg-gray-100 rounded"
      >
        →
      </button>
    </div>
  );

  return (
    <div className="w-full">
      {label && (
        <label className="block text-[#35424D] mb-[4px] h-[16px] text-left">
          {label}
        </label>
      )}
      <div className="relative w-full max-w-[310px]">
        <DatePicker
          selected={dateValue && isValid(dateValue) ? dateValue : null}
          onChange={(date) => {
            if (!date || isNaN(date)) {
              onChange({ target: { name, value: "" } });
            } else {
              const formatted = format(date, "yyyy-MM-dd");
              onChange({ target: { name, value: formatted } });
            }
          }}
          onKeyDown={handleKeyDown}
          onChangeRaw={handleChangeRaw}
          dateFormat="MM/dd/yyyy"
          placeholderText="MM/DD/YYYY"
          className={`mt-2 p-2 pr-10 border w-full max-w-[310px] h-[40px] border-[#C3C3C3] rounded-md cursor-pointer ${
            disabled
              ? "border-[#C3C3C3] bg-[#F5F5F5] cursor-not-allowed"
              : "cursor-pointer"
          }  focus:border-[#35424D] focus:border focus:outline-none`}
          wrapperClassName="w-full"
          disabled={disabled}
          showYearDropdown
          scrollableYearDropdown
          popperPlacement="top-end"
          yearDropdownItemNumber={100}
          popperClassName="custom-datepicker-popper"
          popperProps={{
            style: {
              zIndex: 99999,
            },
          }}
          renderCustomHeader={CustomHeader}
        >
          <div className="border-t p-2 bg-gray-50">
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 px-4 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded border border-gray-300"
            >
              Clear
            </button>
          </div>
        </DatePicker>
        <style jsx>{`
          .custom-datepicker-popper {
            z-index: 99999 !important;
          }
          .react-datepicker__input-container input[disabled] {
            cursor: not-allowed !important;
            caret-color: transparent !important;
            background-color: #f5f5f5;
            color: #9ca3af;
          }
          .react-datepicker__input-container input:not([disabled]) {
            cursor: pointer;
            caret-color: auto;
          }

          .react-datepicker__navigation--years {
            position: relative;
            width: 0;
            height: 0;
            border: 8px solid transparent;
            cursor: pointer;
            margin: 4px auto;
          }
          .react-datepicker__navigation--years-upcoming {
            border-bottom-color: #666;
            border-top: 0;
            margin-bottom: 2px;
          }
          .react-datepicker__navigation--years-upcoming:hover {
            border-bottom-color: #333;
          }
          .react-datepicker__navigation--years-previous {
            border-top-color: #666;
            border-bottom: 0;
            margin-top: 2px;
          }
          .react-datepicker__navigation--years-previous:hover {
            border-top-color: #333;
          }
        `}</style>

        <FaRegCalendarAlt className="absolute right-3 top-[56%] transform -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
      </div>

      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

const parseDateValueforTime = (value) => {
  if (!value) return null;

  try {
    return toZonedTime(value, "UTC");
  } catch {
    return null;
  }
};

const DateTimeInput = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  previousValue,
}) => {
  const dateValue = parseDateValueforTime(value);
  const handleClear = () => {
    onChange({ target: { name, value: "" } });
  };

  const CustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="flex items-center justify-between px-2 py-2 bg-white border-b">
      <button
        type="button"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="p-1 hover:bg-gray-100 rounded"
      >
        ←
      </button>
      <div className="flex items-center space-x-2">
        <select
          value={date.getMonth()}
          onChange={({ target: { value } }) => changeMonth(parseInt(value))}
          className="text-sm border rounded px-2 py-1"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i}>
              {new Date(0, i).toLocaleDateString("en", { month: "long" })}
            </option>
          ))}
        </select>
        <select
          value={date.getFullYear()}
          onChange={({ target: { value } }) => changeYear(parseInt(value))}
          className="text-sm border rounded px-2 py-1"
        >
          {Array.from({ length: 10 }, (_, i) => {
            const year = new Date().getFullYear() - 5 + i;
            return (
              <option key={year} value={year}>
                {year}
              </option>
            );
          })}
        </select>
      </div>
      <button
        type="button"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="p-1 hover:bg-gray-100 rounded"
      >
        →
      </button>
    </div>
  );

  return (
    <div>
      <label className="block text-[#35424D] mb-[4px] h-[16px] text-left">
        {label}
      </label>
      <div className="relative w-full max-w-[310px]">
        <DatePicker
          selected={dateValue}
          onChange={(date) => {
            if (!date || isNaN(date)) {
              onChange({ target: { name, value: "" } });
            } else {
              const formatted = formatInTimeZone(
                date,
                "UTC",
                "yyyy-MM-dd'T'HH:mm:ssXXX"
              );
              onChange({
                target: {
                  name,
                  value: formatted,
                },
              });
            }
          }}
          showTimeSelect
          timeFormat="HH:mm"
          timeIntervals={15}
          timeCaption="Time"
          dateFormat="MM/dd/yyyy HH:mm"
          placeholderText="MM/DD/YYYY HH:mm AM/PM"
          className={`mt-2 p-2 pr-10 border w-full max-w-[310px] h-[40px] border-[#C3C3C3] rounded-md ${
            disabled ? "border-[#C3C3C3] bg-[#F5F5F5] cursor-not-allowed" : ""
          }`}
          disabled={disabled}
          popperClassName="custom-datepicker-popper"
          popperProps={{
            style: {
              zIndex: 99999,
            },
          }}
          renderCustomHeader={CustomHeader}
        >
          <div className="border-t p-2 bg-gray-50">
            <button
              type="button"
              onClick={handleClear}
              className="w-full py-2 px-4 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded border border-gray-300"
            >
              Clear
            </button>
          </div>
        </DatePicker>
        <style jsx>{`
          .custom-datepicker-popper {
            z-index: 99999 !important;
          }
          .react-datepicker__input-container input[disabled] {
            cursor: not-allowed !important;
            caret-color: transparent !important;
            background-color: #f5f5f5;
            color: #9ca3af;
          }
          .react-datepicker__input-container input:not([disabled]) {
            cursor: pointer;
            caret-color: auto;
          }
        `}</style>

        <FaRegCalendarAlt className="absolute right-3 top-[56%] transform -translate-y-1/2 text-gray-500 pointer-events-none z-10" />
      </div>
      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] w-[310px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

/**
 * @component SelectInput
 * @description A styled select dropdown with optional placeholder and previous value tracking.
 *
 * @param {string} label - Label text.
 * @param {string} name - Name attribute.
 * @param {string} value - Current value.
 * @param {Function} onChange - Change event handler.
 * @param {string[]} options - List of options.
 * @param {boolean} [required=false] - If true, field is required.
 * @param {boolean} [disabled=false] - If true, field is disabled.
 * @param {string} [placeholder=""] - Placeholder text.
 * @param {string} [previousValue] - Optional previous value.
 */
const SelectInput = ({
  label,
  name,
  value = "",
  onChange,
  options,
  required = false,
  disabled = false,
  placeholder = "",
  previousValue,
  error = "",
  important = false,
}) => {
  return (
    <div className="relative max-w-[310px] w-full">
      <label className="block text-[#35424D] mb-1 h-[16px] text-[16px]">
        {label}{" "}
        {(important || required) && <span className="text-red-500">*</span>}
      </label>
      <div className="relative max-w-[310px] w-full">
        <select
          name={name}
          value={value || ""}
          onChange={(e) => onChange(e)}
          className={`w-full px-4 py-2 pr-10 border h-[40px] border-[#C3C3C3] rounded-md text-[#868E94] focus:border-[#35424D] focus:border focus:outline-none appearance-none mt-2 ${
            disabled ? "bg-[#F5F5F5] cursor-not-allowed" : "bg-white"
          } ${error ? "border-red-500" : "border-[#C3C3C3]"}
          `}
          required={required}
          disabled={disabled}
        >
          <option value="" className="text-[#868E94]" disabled hidden>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        <div className="absolute inset-y-0 right-3 top-[56%] transform -translate-y-1/2 flex items-center pointer-events-none">
          <FaCaretDown className="text-[20px] text-gray-500" />
        </div>
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px]  font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

const NewNativeSelectInput = ({
  label,
  name,
  value = "",
  onChange,
  options,
  required = false,
  disabled = false,
  placeholder = "",
  previousValue,
  error = "",
  important = false,
}) => {
  const [localSelected, setLocalSelected] = useState(value);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(-1);

  useEffect(() => {
    setLocalSelected(value);
  }, [value]);

  useEffect(() => {
    const handleScroll = () => {
      if (open) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    setHovered(-1);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  return (
    <div className="relative w-full max-w-[310px]">
      <label className="block text-[#35424D] mb-1 h-[16px] text-[16px]">
        {label}{" "}
        {(important || required) && <span className="text-red-500">*</span>}
      </label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(!open)}
            className={`relative w-full h-[40px] mt-2 text-left px-4 py-2 pr-10 border rounded-md overflow-hidden break-all ${
              disabled ? "bg-[#F5F5F5] cursor-not-allowed" : "bg-white"
            } border-[#C3C3C3] text-[#35424D] focus:outline-none focus:border-[#35424D] appearance-none`}
          >
            {localSelected ? (
              <span>{localSelected}</span>
            ) : (
              <span className="text-[#868E94]">
                {placeholder || `Select ${label}`}
              </span>
            )}
            <span className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center pointer-events-none">
              <FaCaretDown className="text-[20px] text-gray-500" />
            </span>
          </button>
        </PopoverTrigger>

        <PopoverContent
          className=" bg-white border-[1px] border-gray-600 overflow-hidden rounded-lg shadow-lg p-0 w-[--radix-popover-trigger-width]"
          align="start"
          onMouseLeave={() => setHovered(-1)}
          sideOffset={0}
        >
          <div className="max-h-[200px] overflow-y-auto text-[#868E94] text-[15px]">
            {options.length > 0 ? (
              options.map((option, index) => (
                <div
                  key={option}
                  className={`p-[3.5px] px-5 rounded cursor-pointer font-extraligh ${
                    localSelected === option ? "bg-[#126acf] text-white" : ""
                  } ${hovered === index ? "bg-[#a1c5ee] text-white" : ""}`}
                  onMouseEnter={() => setHovered(index)}
                  onClick={() => {
                    setHovered(index);
                    setLocalSelected(option);
                    onChange({ target: { name: name, value: option } });
                    setOpen(false);
                  }}
                >
                  <span className="">{option}</span>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm text-center py-2">
                No options found
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px]  font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

const NewSelectInput = ({
  label,
  name,
  value = "",
  onChange,
  options = [],
  required = false,
  disabled = false,
  placeholder = "",
  previousValue,
  important = false,
  popover,
  setPopover,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelected, setLocalSelected] = useState(value);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    setLocalSelected(value);
  }, [value]);

  useEffect(() => {
    if (typeof popover === "boolean") {
      setIsPopoverOpen(popover);
    }
  }, [popover]);

  useEffect(() => {
    if (!isPopoverOpen) {
      setSearchTerm("");
    }
  }, [isPopoverOpen]);

  const filteredOptions = useMemo(() => {
    const base = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return [{ label: `Select ${label}`, value: "__clear__" }, ...base];
  }, [options, searchTerm, label]);

  const handleSelect = (val) => {
    const finalValue = val === "__clear__" ? "" : val;
    setLocalSelected(finalValue);
    onChange({ target: { name, value: finalValue } });

    setIsPopoverOpen(false);
    if (typeof setPopover === "function") {
      setPopover(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      // Skip the first option which is "Select {label}" and select the first real option
      if (filteredOptions.length > 1) {
        handleSelect(filteredOptions[1].value);
      }
    }
  };

  const open = typeof popover === "boolean" ? popover : isPopoverOpen;
  const onOpenChange = (next) => {
    if (typeof setPopover === "function") {
      setPopover(next);
    } else {
      setIsPopoverOpen(next);
    }
  };

  return (
    <div className="relative w-full max-w-[310px]">
      <label className="block text-[#35424D] mb-1 h-[16px] text-[16px]">
        {label}{" "}
        {(important || required) && <span className="text-red-500">*</span>}
      </label>

      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={`w-full h-[40px] mt-2 text-left px-4 py-2 pr-10 border rounded-md overflow-hidden break-all ${
              disabled ? "bg-[#F5F5F5] cursor-not-allowed" : "bg-white"
            } border-[#C3C3C3] text-[#35424D] focus:outline-none focus:border-[#35424D] appearance-none`}
          >
            {options
              .find((opt) => opt.value === localSelected)
              ?.label.split(" - ")[0] || (
              <span className="text-[#868E94]">
                {placeholder || `Select ${label}`}
              </span>
            )}
            <span className="absolute inset-y-0 right-3 top-[69%] transform -translate-y-1/2 flex items-center pointer-events-none">
              <FaCaretDown className="text-[20px] text-gray-500" />
            </span>
          </button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[310px] bg-white border border-gray-300 rounded-lg shadow-lg z-50 p-0"
          align="start"
          sideOffset={4}
        >
          <div className="p-2">
            <input
              type="text"
              placeholder="Search..."
              className="w-full p-2 h-[28px] border border-[#C3C3C3] rounded text-[13px] focus:border-[#35424D] focus:outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyPress}
              autoFocus
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto text-[13px] px-2">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={`p-2 rounded cursor-pointer hover:bg-gray-100 ${
                    localSelected === option.value
                      ? "bg-gray-100 font-semibold"
                      : ""
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span className="text-[#35424D]">{option.label}</span>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm text-center py-2">
                No options found
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

/**
 * @component NumberInput
 * @description A number input field with validation and previous value display.
 *
 * @param {string} label - Field label.
 * @param {string} name - Input name.
 * @param {number|string} value - Input value.
 * @param {Function} onChange - Change handler.
 * @param {boolean} [required=false] - Whether the field is required.
 * @param {boolean} [disabled=false] - Whether the input is disabled.
 * @param {string} [placeholder=""] - Placeholder text.
 * @param {string} [previousValue] - Previous value for change tracking.
 */
const NumberInput = ({
  label,
  name,
  value = "",
  onChange,
  required = false,
  disabled = false,
  placeholder = "",
  previousValue,
  important = false,
  maxValue,
}) => {
  const handleChange = (e) => {
    const val = e.target.value;

    // Reject non-digits and decimals
    if (/^\d*$/.test(val) && (!maxValue || Number(val) <= maxValue)) {
      onChange(e);
    }
  };

  const handleKeyDown = (e) => {
    // Prevent '.', '-', 'e', etc. — which are allowed in type="number"
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div>
      <label className="block h-[16px] mb-1 text-[#35424D] text-[16px]">
        {label}{" "}
        {(important || required) && <span className="text-red-500">*</span>}
      </label>
      <input
        type="number"
        name={name}
        value={value || ""}
        step="1"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={`text-[#35424D] w-full max-w-[310px] h-[40px] mt-2 p-2 border border-[#C3C3C3] rounded-md focus:border-[#35424D] focus:outline-none ${
          disabled ? "border-[#C3C3C3] bg-[#F5F5F5] cursor-not-allowed" : ""
        }`}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
      />

      {previousValue && (
        <p className="flex items-start gap-2 text-[#B95000] text-[14px] font-semibold mt-1 leading-snug">
          <BiSolidError className="min-w-[16px] mt-[2px]" />
          <span>{previousValue}</span>
        </p>
      )}
    </div>
  );
};

const DateFilter = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  placeholder = "Select date",
}) => {
  return (
    <div className="date-input-wrapper">
      {label && <label htmlFor={name}>{label}</label>}
      <DatePicker
        id={name}
        selected={value ? new Date(value) : null}
        onChange={(date) => onChange({ target: { name, value: date } })}
        placeholderText={placeholder}
        dateFormat="yyyy-MM-dd"
        disabled={disabled}
        popperClassName="custom-datepicker-popper"
        calendarClassName="custom-datepicker-calendar"
      />

      <style jsx>{`
        .date-input-wrapper {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        label {
          font-size: 0.85rem;
          font-weight: 500;
          color: #374151;
        }

        /* Styling for popover position & z-index */
        .custom-datepicker-popper {
          z-index: 99999 !important;
        }

        /* Main calendar popup sizing */
        .custom-datepicker-popper .react-datepicker {
          width: 20rem !important; /* popover width */
          height: 20rem !important; /* popover height */
          overflow: auto; /* scroll if content bigger */
          font-size: 0.8rem; /* shrink font */
        }

        /* Shrink day cells to fit smaller height */
        .custom-datepicker-popper .react-datepicker__day,
        .custom-datepicker-popper .react-datepicker__day-name {
          width: 2rem;
          line-height: 2rem;
          margin: 0.1rem;
        }

        /* Adjust header for smaller popup */
        .custom-datepicker-popper .react-datepicker__header {
          padding-top: 4px;
          padding-bottom: 4px;
        }

        /* Disabled input styles */
        .react-datepicker__input-container input[disabled] {
          cursor: not-allowed !important;
          caret-color: transparent !important;
          background-color: #f5f5f5;
          color: #9ca3af;
        }
        .react-datepicker__input-container input:not([disabled]) {
          cursor: pointer;
          caret-color: auto;
        }
      `}</style>
    </div>
  );
};

export default NumberInput;
export {
  TextInput,
  TextArea,
  DateInput,
  RadioInput,
  SelectInput,
  NumberInput,
  NewSelectInput,
  DateTimeInput,
  NewNativeSelectInput,
};
